'use client';

import { useRef, useState } from 'react';

import {
  type CarouselHandle,
  Carousel,
} from '@/components/atoms/Carousel';
import Section from '@/components/atoms/Section/Section';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { MenuCarouselPagination } from './MenuCarouselPagination';
import { MenuCarouselItem, MenuCarouselItemProps } from './MenuCarouseltem';

export type MenuCarouselProps = {
  items: Pick<MenuCarouselItemProps, 'image' | 'link'>[];
};

export function MenuCarousel({ items }: MenuCarouselProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const carouselRef = useRef<{ swiper: CarouselHandle } | null>(null);

  const slidesRefMap = useRef(new Map<number, HTMLAnchorElement | null>());

  const updateSlide = (index: number) => {
    if (!carouselRef.current) return;
    carouselRef.current.swiper.slideTo(index);
    setCurrentSlideIndex(index);
    slidesRefMap.current.get(index)?.focus();
  };

  return (
    <Section
      data-tracker-section={SECTION_NAMES.MENU_CAROUSEL}
      className='flex flex-col items-center gap-y-3'
    >
      <Carousel
        ref={carouselRef}
        sideGradient={false}
        classNames={{
          root: 'w-full aspect-[300/158]',
          swiper: 'w-full',
          swiperSlide: 'w-full',
        }}
        config={{
          'aria-label': 'Menu Carousel',
          role: 'region',
          autoplay: {
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          },
          loop: true,
          onRealIndexChange: ({ realIndex }) => setCurrentSlideIndex(realIndex),
        }}
        items={items}
        renderItem={({ item, index: i }) => (
          <MenuCarouselItem
            key={i}
            image={item.image}
            link={item.link}
            isActive={currentSlideIndex === i}
            ref={(node) => {
              slidesRefMap.current.set(i, node);
              return () => {
                slidesRefMap.current.delete(i);
              };
            }}
          />
        )}
      />
      {/* TODO: separately fix the weird behaviour when clicking 1 -> 3 */}
      <MenuCarouselPagination
        activeIndex={currentSlideIndex}
        itemCount={items.length}
        handlePageChange={updateSlide}
      />
    </Section>
  );
}
